/**
 * ============================================================================
 * useKbAcl — Hooks para gerenciar ACL da Knowledge Base (Fase C).
 *
 * Expõe:
 *   - useKbAclList(scope, scopeId)          → lista concessões atuais
 *   - useKbAclHasCustom(scope, scopeId)     → true se existe entrada não-herdada
 *   - useKbGrantAccess()                    → mutation p/ conceder
 *   - useKbRevokeAccess()                   → mutation p/ revogar
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v1.2.0 - Expõe target_email e target_avatar_url da RPC
 *            kb_list_page_access v1.2.0 (para renderizar SmartAvatar na UI).
 *   RELEASE: 2026-07-08 v1.1.0 - Fase D: adiciona sujeito 'profile' (permission_profiles).
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial (Fase C).
 * ============================================================================
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

export type KbAclScope = "page" | "collection" | "space";
export type KbAclTargetType = "member" | "external" | "group" | "profile";
export type KbAclLevel = "read" | "comment" | "edit" | "admin";

export interface KbAclRow {
  acl_id: string;
  scope: KbAclScope;
  target_type: KbAclTargetType;
  target_id: string;
  target_display_name: string | null;
  /** E-mail real do sujeito (membro/externo). NULL para profile/group. */
  target_email: string | null;
  /** URL do avatar do membro (profiles.avatar_url). NULL para os demais tipos. */
  target_avatar_url: string | null;
  access_level: KbAclLevel;
  inherited_from_space: boolean;
  created_at: string;
}

const listKey = (scope: KbAclScope, scopeId: string) => ["kb-acl", scope, scopeId] as const;

/** Lista as concessões de um escopo (page/collection/space). */
export function useKbAclList(scope: KbAclScope, scopeId: string | null | undefined) {
  return useQuery({
    queryKey: listKey(scope, scopeId ?? ""),
    enabled: !!scopeId,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)("kb_list_page_access", {
        p_scope: scope,
        p_scope_id: scopeId,
      });
      if (error) {
        logger.error("[useKbAclList] rpc failed", error);
        throw error;
      }
      return (data ?? []) as KbAclRow[];
    },
    staleTime: 30_000,
  });
}

/** Concede acesso a um alvo. */
export function useKbGrantAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      scope: KbAclScope;
      scopeId: string;
      targetType: KbAclTargetType;
      targetId: string;
      accessLevel: KbAclLevel;
    }) => {
      const { data, error } = await (supabase.rpc as any)("kb_grant_page_access", {
        p_scope: input.scope,
        p_scope_id: input.scopeId,
        p_target_type: input.targetType,
        p_target_id: input.targetId,
        p_access_level: input.accessLevel,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_, vars) => {
      // Invalida a lista do próprio scope
      qc.invalidateQueries({ queryKey: listKey(vars.scope, vars.scopeId) });
      // Também invalida listas de páginas, pois heranças podem ter propagado
      qc.invalidateQueries({ queryKey: ["kb-acl", "page"] });
      toast({ title: "Acesso concedido" });
    },
    onError: (e: any) => {
      logger.error("[useKbGrantAccess] error", e);
      toast({
        title: "Falha ao conceder acesso",
        description: e?.message ?? "Erro desconhecido",
        variant: "destructive",
      });
    },
  });
}

/** Revoga uma ACL (ID direto). */
export function useKbRevokeAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { aclId: string; scope: KbAclScope; scopeId: string }) => {
      const { data, error } = await (supabase.rpc as any)("kb_revoke_page_access", {
        p_acl_id: input.aclId,
      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: listKey(vars.scope, vars.scopeId) });
      qc.invalidateQueries({ queryKey: ["kb-acl", "page"] });
      toast({ title: "Acesso revogado" });
    },
    onError: (e: any) => {
      logger.error("[useKbRevokeAccess] error", e);
      toast({
        title: "Falha ao revogar acesso",
        description: e?.message ?? "Erro desconhecido",
        variant: "destructive",
      });
    },
  });
}
